/* Calls — set one up, and see what is coming.
 *
 * BUILT LIKE THE MESSAGES PAGE, deliberately. Both answer the same question —
 * who is this for — and the same person does both, so they should not feel
 * like two different products. Composer on the page rather than behind a
 * modal, the audience chosen the same way, history underneath.
 *
 * The client drew these three forms by hand (docs/CALL_SCREENS_SPEC.md) and
 * every field here is one of theirs. What changed from their drawing is only
 * where it sits: they sketched three separate screens, and three screens that
 * differ by two checkboxes are one screen with a choice at the top.
 *
 * A CHURCH SCREEN, NOT A GROUP ONE. A leaders' meeting spans groups and a
 * live service is for everybody, so the audience is chosen here rather than
 * inherited from wherever the page happened to live.
 *
 * SCHEDULING ONLY. Nothing here joins a call — that needs the media platform.
 * The controls that will start one are absent rather than present and inert.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  BarChart3,
  CalendarClock,
  Loader2,
  Mic,
  Plus,
  Radio,
  Trash2,
  Users,
  Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ArrangeDialog, useArrangeable, type ArrangeMode } from './ArrangeDialog'
import { OPENS_BEFORE_MS, isOpen, useJoinable } from './UpcomingCalls'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import {
  useCalls,
  useCancelCall,
  useFeatures,
  useMe,
  useMessagingAnalytics,
  useMyCalls,
  useMyPermissions,
} from '@/api/queries'
import { cn } from '@/lib/utils'
import type { CallKind, ScheduledCall } from '@/types/api'

/* Named as people arrange them: a meeting (voice, or with video), or a
   live. See ArrangeDialog. */
const KIND_NAME: Record<CallKind, string> = {
  audio: 'Meeting',
  video: 'Video meeting',
  live: 'Live',
}

const KIND_ICON: Record<CallKind, typeof Mic> = {
  audio: Mic,
  video: Video,
  live: Radio,
}

export function CallsPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null

  /* The same question the server asks. Any one of the three is enough to see
     the page, because it shows all three kinds together. */
  const permissions = useMyPermissions(churchId)
  const held = permissions.data ?? []
  const admin = me?.role === 'church_admin' || me?.role === 'super_admin'
  const mayMeet =
    admin || held.includes('calls.audio.create') || held.includes('calls.video.create')
  const mayLive = admin || held.includes('broadcast.create')
  const maySchedule = mayMeet || mayLive
  const [arranging, setArranging] = useState<ArrangeMode | null>(null)

  const upcoming = useCalls(churchId, true)
  const previous = useCalls(churchId, false)
  const features = useFeatures()

  if (!churchId) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Calls are for a church. Join one to meet with its members.
      </Card>
    )
  }

  if (!features.anyMeetings) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Meetings aren't available for your church.
      </Card>
    )
  }

  if (permissions.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (!maySchedule) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Setting up calls is for administrators. Yours will let you know when
        something is arranged.
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header className="flex flex-wrap items-end justify-between gap-s3">
        <div>
          <h1 className="text-lg font-extrabold tracking-tight">Meetings and lives</h1>
          <p className="text-base text-ink-2">
            Meet your church, or go live for everyone to watch.
          </p>
        </div>
        {/* ONE BUTTON, and the choice behind it. See NewButton. */}
        <NewButton
          mayMeet={mayMeet}
          mayLive={mayLive}
          onPick={setArranging}
        />
      </header>

      <Reach churchId={churchId} />

      {arranging ? (
        <ArrangeDialog
          mode={arranging}
          churchId={churchId}
          onClose={() => setArranging(null)}
        />
      ) : null}

      <History
        upcoming={upcoming.data ?? []}
        previous={previous.data ?? []}
        loading={upcoming.isLoading}
        churchId={churchId}
        canStart={mayMeet || mayLive}
      />
    </div>
  )
}

/* -------------------------------------------------------------- new button
 *
 * "New" — and behind it Meet or Go live, whichever this person may arrange
 * and the church offers. One button rather than two side by side: the choice
 * is made once they have said they want to start something.
 */
export function NewButton({
  mayMeet: allowedMeet,
  mayLive: allowedLive,
  onPick,
}: {
  mayMeet: boolean
  mayLive: boolean
  onPick: (mode: ArrangeMode) => void
}) {
  const arrangeable = useArrangeable()
  const mayMeet = allowedMeet && arrangeable.meet
  const mayLive = allowedLive && arrangeable.live
  if (!mayMeet && !mayLive) return null
  if (mayMeet !== mayLive) {
    return (
      <Button variant="primary" onClick={() => onPick(mayMeet ? 'meet' : 'live')}>
        <Plus className="h-4 w-4" aria-hidden /> New
      </Button>
    )
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="primary">
          <Plus className="h-4 w-4" aria-hidden /> New
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-72 rounded-md bg-surface p-s1 shadow-3"
        >
          <DropdownMenu.Item
            onSelect={() => onPick('meet')}
            className="flex cursor-pointer items-start gap-s3 rounded-sm p-s3 outline-none hover:bg-hover"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-brand-softer text-brand">
              <Users className="h-4 w-4" aria-hidden />
            </span>
            <span>
              <span className="block font-semibold">Meet</span>
              <span className="block text-sm text-ink-2">A conversation — everyone can talk</span>
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => onPick('live')}
            className="flex cursor-pointer items-start gap-s3 rounded-sm p-s3 outline-none hover:bg-hover"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-danger-soft text-danger">
              <Radio className="h-4 w-4" aria-hidden />
            </span>
            <span>
              <span className="block font-semibold">Go live</span>
              <span className="block text-sm text-ink-2">You speak, the church watches</span>
            </span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

/* ---------------------------------------------------------- group meetings
 *
 * A group's own meetings and lives — the Meetings tab on its page. Leaders
 * see everything it has arranged, coming up and finished, and the New
 * button; a member sees what is coming up for them in this group.
 */
export function GroupMeetingsPanel({
  churchId,
  group,
}: {
  churchId: string
  group: { id: string; name: string }
}) {
  const { data: me } = useMe()
  const permissions = useMyPermissions(churchId, group.id)
  const held = permissions.data ?? []
  const admin = me?.role === 'church_admin' || me?.role === 'super_admin'
  const mayMeet =
    admin || held.includes('calls.audio.create') || held.includes('calls.video.create')
  const mayLive = admin || held.includes('broadcast.create')
  const leads = mayMeet || mayLive
  const [arranging, setArranging] = useState<ArrangeMode | null>(null)

  const upcoming = useCalls(leads ? churchId : null, true, group.id)
  const previous = useCalls(leads ? churchId : null, false, group.id)
  const mine = useMyCalls(!leads)

  if (!leads) {
    const rows = (mine.data ?? []).filter((c) => c.group_id === group.id)
    return rows.length ? (
      <div className="space-y-s2">
        {rows.map((call) => (
          <CallRow key={call.id} call={call} churchId={churchId} member />
        ))}
      </div>
    ) : (
      <Card className="p-s8 text-center">
        <CalendarClock className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
        <p className="mt-s3 text-md font-bold">Nothing coming up</p>
        <p className="mx-auto mt-1 max-w-sm text-base text-ink-2">
          When this group meets or goes live, it appears here and you are told.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <div className="flex items-center justify-between">
        <p className="text-base text-ink-2">Meetings and lives for {group.name}.</p>
        <NewButton mayMeet={mayMeet} mayLive={mayLive} onPick={setArranging} />
      </div>
      {arranging ? (
        <ArrangeDialog
          mode={arranging}
          churchId={churchId}
          group={group}
          onClose={() => setArranging(null)}
        />
      ) : null}
      <History
        upcoming={upcoming.data ?? []}
        previous={previous.data ?? []}
        loading={upcoming.isLoading}
        churchId={churchId}
        canStart={mayMeet || mayLive}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ reach
 *
 * How many of the congregation a call could reach at all, which is the
 * number an administrator can act on — the same strip Messages carries, from
 * the same endpoint, because it answers the same question.
 */

function Reach({ churchId }: { churchId: string }) {
  const stats = useMessagingAnalytics(churchId)
  if (!stats.data) return null
  const s = stats.data

  return (
    <Card className="flex flex-wrap items-center gap-x-s5 gap-y-s2 p-s4">
      <Stat value={s.members} label="members" />
      <Stat
        value={s.push_reachable}
        label="can be told on their phone"
        warn={s.members > 0 && s.push_reachable * 2 < s.members}
      />
    </Card>
  )
}

function Stat({
  value,
  label,
  warn,
}: {
  value: number | string
  label: string
  warn?: boolean
}) {
  return (
    <div>
      <div
        className={cn(
          'text-lg font-extrabold tracking-tight',
          warn && 'text-warning-ink',
        )}
      >
        {value}
      </div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}

/* ---------------------------------------------------------------- history */

function History({
  upcoming,
  previous,
  loading,
  churchId,
  canStart,
}: {
  upcoming: ScheduledCall[]
  previous: ScheduledCall[]
  loading: boolean
  churchId: string
  canStart: boolean
}) {
  if (loading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  /* A recording is a finished call that was recorded, so it is the same
     row listed again rather than a separate thing to fetch. */
  const recorded = previous.filter((c) => c.recording)

  if (!upcoming.length && !previous.length) {
    return (
      <Card className="p-s8 text-center">
        <CalendarClock className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
        <p className="mt-s3 text-md font-bold">Nothing arranged yet</p>
        {canStart ? (
          <p className="mx-auto mt-1 max-w-sm text-base text-ink-2">
            Start one with New, and everybody you choose will be told about it.
          </p>
        ) : null}
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      {upcoming.length ? (
        <section className="space-y-s2">
          <h2 className="px-s1 text-md font-bold">Coming up</h2>
          {upcoming.map((call) => (
            <CallRow
              key={call.id}
              call={call}
              churchId={churchId}
              cancellable
            />
          ))}
        </section>
      ) : null}

      {previous.length ? (
        <section className="space-y-s2">
          <h2 className="px-s1 text-md font-bold">Finished</h2>
          {previous.map((call) => (
            <CallRow key={call.id} call={call} churchId={churchId} />
          ))}
        </section>
      ) : null}

      {/* RECORDINGS. The client drew this as its own tab. It is a section
          here instead, for the same reason the three forms became one: a
          second tab bar under the page's own tabs reads as navigation when
          it is really a filter, and a recording IS a finished call — the
          same row, listed again by what it left behind. */}
      {recorded.length ? (
        <section className="space-y-s2">
          <h2 className="px-s1 text-md font-bold">Recordings</h2>
          <p className="px-s1 text-sm text-ink-2">
            Kept for the church to listen back to.
          </p>
          {recorded.map((call) => (
            <CallRow key={call.id} call={call} churchId={churchId} />
          ))}
        </section>
      ) : null}
    </div>
  )
}

function CallRow({
  call,
  churchId,
  cancellable,
  member = false,
}: {
  call: ScheduledCall
  churchId: string
  cancellable?: boolean
  /* A member's own view: the way in, without the report. */
  member?: boolean
}) {
  const cancel = useCancelCall(churchId)
  const [confirming, setConfirming] = useState(false)
  const Icon = KIND_ICON[call.kind]
  const live = call.status === 'live'
  /* Watching a live is always open; hosting one, or joining a meeting,
     follows what the church offers. */
  const features = useFeatures()
  const joinable = useJoinable()
  const enterable =
    call.kind === 'live' ? member || features.live_broadcasts : joinable(call)

  return (
    <Card className={cn('p-s4', call.status === 'ended' && 'opacity-75')}>
      <div className="flex items-start justify-between gap-s3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold">{call.title}</h3>
          {call.description ? (
            <p className="mt-0.5 line-clamp-2 text-base text-ink-2">
              {call.description}
            </p>
          ) : null}
        </div>

        {live ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-danger-soft px-s3 py-0.5 text-xs font-bold text-danger-ink">
            <span className="h-1.5 w-1.5 rounded-pill bg-danger" aria-hidden />
            Live now
          </span>
        ) : call.is_immediate && call.status === 'scheduled' ? (
          <span className="shrink-0 rounded-pill bg-brand-soft px-s3 py-0.5 text-xs font-bold text-brand">
            Starting now
          </span>
        ) : null}
      </div>

      {/* The same facts line the Messages history uses, so the two screens
          read the same way. */}
      <div className="mt-s3 flex flex-wrap items-center gap-x-s3 gap-y-1 text-sm text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {KIND_NAME[call.kind]}
        </span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" aria-hidden />
          {call.audience_label}
        </span>
        <span aria-hidden>·</span>
        <span>{whenLabel(call)}</span>
        {call.recording ? (
          <>
            <span aria-hidden>·</span>
            <span>Recorded</span>
          </>
        ) : null}
        {call.max_participants ? (
          <>
            <span aria-hidden>·</span>
            <span>up to {call.max_participants}</span>
          </>
        ) : null}
        {call.moderator_name ? (
          <>
            <span aria-hidden>·</span>
            <span>{call.moderator_name} moderates</span>
          </>
        ) : null}
        {call.created_by_name ? (
          <>
            <span aria-hidden>·</span>
            <span>{call.created_by_name}</span>
          </>
        ) : null}
      </div>

      {/* The report exists from the moment the call is announced — reach is
          a real answer before anybody has joined anything. */}
      <div className="mt-s3 flex items-center justify-between gap-s3 border-t border-line-2 pt-s3">
        <div className="flex items-center gap-s3">
          {/* THE WAY IN. Whoever arranged a call is the likeliest person to
              open it, and until now this row had no door. */}
          {/* NOT OPEN YET: a meeting can be entered from a quarter of an
              hour before it starts, so until then the row says when. */}
          {call.status !== 'ended' && call.kind !== 'live' && !isOpen(call) ? (
            <span className="text-sm font-semibold text-ink-3">
              Opens at{' '}
              {new Date(
                new Date(call.starts_at!).getTime() - OPENS_BEFORE_MS,
              ).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          ) : call.status !== 'ended' && enterable ? (
            <Button asChild size="sm" variant={live ? 'primary' : 'secondary'}>
              {/* A live is broadcast, not joined: its door is the studio. */}
              <Link
                to={`/calls/${call.id}/${call.kind === 'live' ? 'live' : 'room'}`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {call.kind === 'live'
                  ? member
                    ? 'Watch'
                    : live
                      ? 'Open the live'
                      : 'Open the studio'
                  : 'Join now'}
              </Link>
            </Button>
          ) : null}
          {member ? null : (
          <Link
            to={`/calls/${call.id}/report`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
          >
            <BarChart3 className="h-3.5 w-3.5" aria-hidden />
            How it did
          </Link>
          )}
        </div>
        {cancellable ? (
          <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Cancel
          </Button>
        ) : null}
      </div>

      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title="Cancel this call?"
        body="It comes off the list, and nobody will be reminded about it."
        confirmLabel="Cancel it"
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate(call.id)}
      />
    </Card>
  )
}

/** "Sat 27 Sept, 11:00", or "Starting now", or when it finished. */
function whenLabel(call: ScheduledCall): string {
  if (call.status === 'ended' && call.ended_at) {
    return `ended ${new Date(call.ended_at).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    })}`
  }
  if (!call.starts_at) return 'now'
  return new Date(call.starts_at).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}
